"use client";

import { useEffect, useState } from "react";
import { getTenant, parseTenantId, TENANTS, DEFAULT_TENANT, type TenantConfig } from "@/lib/tenants";

/**
 * Resolves tenant from cookie (set by proxy.ts) with hostname fallback.
 * On the very first page load the cookie may not exist yet, so we read
 * the subdomain directly from window.location.hostname.
 *
 * DEV OVERRIDE: append ?tenant=<id> to any localhost URL to switch layer.
 * Examples:
 *   http://localhost:3000/?tenant=www        → Public marketing website
 *   http://localhost:3000/?tenant=account    → Customer Account Portal
 *   http://localhost:3000/?tenant=ockham     → Ockham tenant workspace
 *   http://localhost:3000/portal/dashboard?tenant=portal → Client Portal
 *   http://localhost:3000/mdt/dashboard?tenant=mdt       → MDT App
 * The ?tenant param sets the cookie so it persists for the session.
 */
function resolveTenantId(): string {
  if (typeof window === "undefined") return "demo";

  // 0. Dev override: ?tenant=xxx on localhost sets cookie and takes priority
  if (window.location.hostname.includes("localhost")) {
    const params = new URLSearchParams(window.location.search);
    const override = params.get("tenant");
    if (override) {
      document.cookie = `tenant-id=${override}; path=/; max-age=86400`;
      return override;
    }
  }

  // 1. Try cookie (most reliable after first load)
  const fromCookie = parseTenantId(document.cookie);
  if (fromCookie !== "demo" || document.cookie.includes("tenant-id=demo")) {
    return fromCookie;
  }

  // 2. Fallback: read subdomain from hostname
  const host = window.location.hostname;
  if (!host.includes("localhost") && !host.includes(".vercel.app")) {
    const parts = host.split(".");
    const sub = parts[0];
    if (sub === "www" || parts.length === 2) return "www";
    // Special routing IDs that are not operational tenant workspaces
    if (sub === "account" || sub === "portal") return sub;
    if (TENANTS[sub]) return sub;
  }

  return "demo";
}

/**
 * SSR-safe: both server render AND first client render return DEFAULT_TENANT.
 * The real tenant is resolved in useEffect (post-hydration) so the trees match.
 * This prevents React error #418 (hydration mismatch) freezing the whole app.
 */
export function useTenant(): TenantConfig {
  const [tenant, setTenant] = useState<TenantConfig>(DEFAULT_TENANT);
  useEffect(() => {
    setTenant(getTenant(resolveTenantId()));
  }, []);
  return tenant;
}

export function useTenantId(): string {
  const [id, setId] = useState<string>("demo");
  useEffect(() => {
    setId(resolveTenantId());
  }, []);
  return id;
}

